import { Injectable, linkedSignal, signal, untracked } from '@angular/core';
import { intersects as radashIntersects, unique as radashUnique } from 'radash';

import {
    BayDrawer,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import {
    SynopticBayPlanItem,
    SynopticBoxPlanCable,
    SynopticBoxPlanCableBySide,
} from '@connect-services/client/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class SynopticBayPlanEditService {
    public readonly cassettes = signal<SynopticBayPlanItem[][]>([]);
    public readonly drawers = linkedSignal<SynopticBayPlanItem[][], BayDrawer[]>({
        computation: (cassettes: SynopticBayPlanItem[][], previous): BayDrawer[] => {
            return this.groupCassettesByDrawers(
                cassettes,
                untracked(() => this.sideCables()),
                previous?.value,
            );
        },
        source: this.cassettes,
    });
    public readonly sideCables = signal<SynopticBoxPlanCableBySide>({ enteringCables: [], outgoingCables: [] });

    public updateDrawerCables(cables: SynopticBoxPlanCableBySide, drawerId: string | null): void {
        this.sideCables.update((currentCables: SynopticBoxPlanCableBySide) => {
            return {
                enteringCables: radashUnique(
                    [...currentCables.enteringCables, ...cables.enteringCables],
                    (cable: SynopticBoxPlanCable) => cable.code,
                ),
                outgoingCables: radashUnique(
                    [...currentCables.outgoingCables, ...cables.outgoingCables],
                    (cable: SynopticBoxPlanCable) => cable.code,
                ),
            };
        });

        this.drawers.update((currentDrawers: BayDrawer[]) => {
            return currentDrawers.map((drawer: BayDrawer) => {
                if (drawer.id === drawerId) {
                    drawer.cables = cables;
                }

                return drawer;
            });
        });
    }

    public updateDrawerCassettes(cassettes: SynopticBayPlanItem[][], drawerId: string | null): void {
        this.cassettes.update((currentCassettes: SynopticBayPlanItem[][]) => {
            const newElements = cassettes.filter((newCassettesItems: SynopticBayPlanItem[]) =>
                currentCassettes.every((currentCassetteItems: SynopticBayPlanItem[]) => {
                    return !radashIntersects(
                        currentCassetteItems.map((item: SynopticBayPlanItem) => item.cassette),
                        newCassettesItems.map((item: SynopticBayPlanItem) => item.cassette),
                    );
                }),
            );

            return currentCassettes
                .map((currentCassetteItems: SynopticBayPlanItem[]) => {
                    if (!currentCassetteItems.length || currentCassetteItems[0].ti_uuid !== drawerId) {
                        return currentCassetteItems;
                    }

                    return cassettes.flat().filter((newCassetteItem: SynopticPlanItem) => {
                        return newCassetteItem.cassette === currentCassetteItems[0].cassette;
                    });
                })
                .concat(newElements);
        });
    }

    private getDrawerCables(
        allCables: SynopticBoxPlanCableBySide,
        cassetteItem: SynopticBayPlanItem,
        currentCables?: SynopticBoxPlanCableBySide,
    ): SynopticBoxPlanCableBySide {
        const currentOngoingCodes: string[] =
            currentCables?.enteringCables.map((cable: SynopticBoxPlanCable) => cable.code) ?? [];
        const currentOutgoingCodes: string[] =
            currentCables?.outgoingCables.map((cable: SynopticBoxPlanCable) => cable.code) ?? [];

        return {
            enteringCables: allCables.enteringCables.filter((cable: SynopticBoxPlanCable) => {
                return cable.code === cassetteItem.cable_entrant || currentOngoingCodes.includes(cable.code);
            }),
            outgoingCables: allCables.outgoingCables.filter((cable: SynopticBoxPlanCable) => {
                return cable.code === cassetteItem.cable_sortant || currentOutgoingCodes.includes(cable.code);
            }),
        };
    }

    private groupCassettesByDrawers(
        cassettes: SynopticBayPlanItem[][],
        cables: SynopticBoxPlanCableBySide,
        previousDrawers?: BayDrawer[],
    ): BayDrawer[] {
        return cassettes.flat().reduce((acc: BayDrawer[], cassetteItem: SynopticBayPlanItem) => {
            const existingItem = acc.find((drawer: BayDrawer) => drawer.id === cassetteItem.ti_uuid);

            if (!existingItem) {
                const previousDrawerItem = previousDrawers?.find(
                    (previousDrawer: BayDrawer) => previousDrawer.id === cassetteItem.ti_uuid,
                );
                acc.push({
                    cables: this.getDrawerCables(cables, cassetteItem, previousDrawerItem?.cables),
                    cassettes: [[cassetteItem]],
                    code: cassetteItem.tiroir,
                    id: cassetteItem.ti_uuid,
                    length: Number(cassetteItem.taille_tiroir) || 0,
                    position: cassetteItem.position_tiroir,
                    reference: cassetteItem.reference_tiroir,
                    side: cassetteItem.cote_tiroir,
                });

                return acc;
            }

            const index = existingItem.cassettes.findIndex(
                (item: SynopticBayPlanItem[]) => item[0].cassette === cassetteItem.cassette,
            );
            if (index !== -1) {
                existingItem.cassettes[index].push(cassetteItem);
            } else {
                existingItem.cassettes.push([cassetteItem]);
            }
            existingItem.cables = this.getDrawerCables(cables, cassetteItem, existingItem.cables);

            return acc;
        }, []);
    }
}
